/**
 * notifications.service — MSR in-app alerts + Web Push subscription management.
 * In-app reads/updates go straight to user_alerts (RLS scopes to auth.uid()).
 * Push subscribe/unsubscribe go through the register-push-subscription edge fn
 * (which binds the subscription to the caller's profile server-side).
 */
import { supabase } from "./supabase";
import type {
  UserAlert,
  NotificationPreferences,
  EnablePushResult,
} from "../types/notifications.types";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as
  | string
  | undefined;

// ---------------- in-app alerts ----------------
export async function listUserAlerts(limit = 30): Promise<UserAlert[]> {
  const { data, error } = await supabase
    .from("user_alerts")
    .select(
      "id,user_id,alert_type,title,message,link_url,is_read,created_at,source_type,source_id",
    )
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) {
    throw error;
  }
  return (data ?? []) as UserAlert[];
}

export async function markAlertRead(id: string): Promise<void> {
  const { error } = await supabase
    .from("user_alerts")
    .update({ is_read: true })
    .eq("id", id);
  if (error) {
    throw error;
  }
}

export async function markAllAlertsRead(): Promise<void> {
  const { error } = await supabase
    .from("user_alerts")
    .update({ is_read: true })
    .eq("is_read", false);
  if (error) {
    throw error;
  }
}

// ---------------- preferences ----------------
export async function getNotificationPreferences(): Promise<NotificationPreferences | null> {
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("user_id,push_enabled,in_app_enabled")
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data as NotificationPreferences) ?? null;
}

export async function setNotificationPreferences(
  userId: string,
  prefs: { push_enabled?: boolean; in_app_enabled?: boolean },
): Promise<void> {
  const { error } = await supabase
    .from("notification_preferences")
    .upsert({ user_id: userId, ...prefs }, { onConflict: "user_id" });
  if (error) {
    throw error;
  }
}

// ---------------- Web Push ----------------
export function isPushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}
export function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}
export function isStandalonePWA(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function enablePush(): Promise<EnablePushResult> {
  if (!isPushSupported()) {
    return { ok: false, reason: "unsupported" };
  }
  // iOS only delivers Web Push to an INSTALLED PWA (Add to Home Screen).
  if (isIOS() && !isStandalonePWA()) {
    return { ok: false, reason: "ios-needs-install" };
  }
  if (!VAPID_PUBLIC_KEY) {
    return { ok: false, reason: "no-vapid" };
  }
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { ok: false, reason: "denied" };
    }
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    const json = sub.toJSON();
    const { error } = await supabase.functions.invoke(
      "register-push-subscription",
      {
        body: {
          action: "subscribe",
          subscription: { endpoint: json.endpoint, keys: json.keys },
        },
      },
    );
    if (error) {
      return { ok: false, reason: "error", detail: error.message };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "error", detail: String(e) };
  }
}

export async function disablePush(): Promise<void> {
  if (!isPushSupported()) {
    return;
  }
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await supabase.functions.invoke("register-push-subscription", {
      body: { action: "unsubscribe", endpoint: sub.endpoint },
    });
    await sub.unsubscribe();
  }
}

export async function currentPushSubscribed(): Promise<boolean> {
  if (!isPushSupported()) {
    return false;
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    return (await reg.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}
